import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui';

type GenerateImageModalProps = {
  title: string;
  intro: string;
  label: string;
  placeholder: string;
  suggestions: string[];
  initialPrompt?: string;
  generating?: boolean;
  onClose: () => void;
  onGenerate: (_prompt: string) => Promise<void>;
};

/**
 * Shared AI image-generation prompt modal.
 *
 * Used for the sign-off / CTA image, the brand icon, and the profile picture so
 * the three flows share identical UX. Only the copy and quick-idea suggestions
 * differ per caller.
 */
export function GenerateImageModal({
  title,
  intro,
  label,
  placeholder,
  suggestions,
  initialPrompt = '',
  generating = false,
  onClose,
  onGenerate,
}: GenerateImageModalProps) {
  const [prompt, setPrompt] = useState(initialPrompt);
  const [error, setError] = useState('');

  if (typeof document === 'undefined') return null;

  const handleSubmit = async () => {
    setError('');
    try {
      await onGenerate(prompt.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start generation');
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0, color: 'var(--color-neutral-600)' }}>{intro}</p>

          <label
            htmlFor="generate-image-prompt"
            style={{
              display: 'block',
              fontWeight: 600,
              fontSize: 'var(--text-sm)',
              marginBottom: 6,
            }}
          >
            {label}
          </label>
          <textarea
            id="generate-image-prompt"
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={placeholder}
            disabled={generating}
            style={{
              width: '100%',
              padding: 12,
              borderRadius: 8,
              border: '1px solid var(--color-neutral-200)',
              fontFamily: 'inherit',
              fontSize: 'var(--text-sm)',
              resize: 'vertical',
            }}
          />

          {suggestions.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-neutral-500)',
                  marginBottom: 6,
                }}
              >
                Quick ideas
              </p>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="cdw-overlay-icon-btn"
                    onClick={() => setPrompt(s)}
                    disabled={generating}
                    style={{ fontSize: 'var(--text-xs)', padding: '6px 10px' }}
                  >
                    {s.length > 48 ? `${s.slice(0, 48)}…` : s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--color-neutral-500)',
              marginTop: 12,
              marginBottom: 0,
            }}
          >
            Generating uses one image credit and a few tokens.
          </p>

          {error && (
            <p style={{ color: 'var(--color-danger-600)', fontSize: 'var(--text-sm)' }}>{error}</p>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={generating}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={generating}>
            {generating ? 'Generating…' : '✨ Generate'}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
